import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ChevronRight, CalendarDays, ExternalLink } from "lucide-react";
import { Eyebrow, Reveal, MaskLineView, ArrowCta, Faq } from "../components/ui-bits";
import JsonLd from "../components/JsonLd";
import { getArticle, ARTICLE_META } from "../content/articles";
import AnalystClosing from "../components/AnalystClosing";
import { useLang } from "../i18n/LangContext";

const SITE = "https://verdecapital.eu";

export default function ArticlePage({ slug: slugProp }) {
  const params = useParams();
  const { lang, lp } = useLang();
  const slug = slugProp || params.slug;
  const art = getArticle(slug);
  if (!art) return <Navigate to={lp("/insights")} replace />;
  const fallback = lang !== "en" && !art[lang];
  const c = art[lang] || art.en;
  const meta = ARTICLE_META[slug][lang] || ARTICLE_META[slug].en;
  const url = `${SITE}${lp(`/insights/${slug}`)}`;

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}${lp("/")}` },
      { "@type": "ListItem", position: 2, name: lang === "pt" ? "Insights" : "Insights", item: `${SITE}${lp("/insights")}` },
      { "@type": "ListItem", position: 3, name: c.h1 },
    ],
  };
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: c.h1,
    description: meta.description,
    author: { "@type": "Organization", name: "Verde Capital" },
    publisher: { "@type": "Organization", name: "Verde Capital", url: SITE },
    mainEntityOfPage: url,
    datePublished: "2026-10-14",
    dateModified: "2026-10-14",
  };

  return (
    <div className="bg-cream">
      <JsonLd data={breadcrumb} />
      <JsonLd data={articleLd} />
      {c.sections.some((s) => s.faq && s.faqs) && (
        <JsonLd data={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: c.sections.find((s) => s.faq).faqs.map((f) => ({
            "@type": "Question", name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }} />
      )}

      <section className="pt-32 sm:pt-40 pb-10">
        <div className="container-x max-w-3xl">
          {fallback ? (
            <p className="mb-6 rounded-xl border border-sand bg-white px-4 py-3 text-xs leading-relaxed text-fog" data-testid="article-lang-fallback">
              Dit artikel is momenteel beschikbaar in het Engels en het Portugees. De Nederlandse vertaling volgt.
            </p>
          ) : null}
          <nav className="flex items-center gap-1.5 text-xs text-fog" aria-label="Breadcrumb" data-testid="article-breadcrumb">
            <Link to={lp("/")} className="transition-colors hover:text-verde-800">{lang === "pt" ? "Início" : lang === "nl" ? "Home" : "Home"}</Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={lp("/insights")} className="transition-colors hover:text-verde-800">Insights</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-ink truncate max-w-[180px]">{c.h1}</span>
          </nav>
          <h1 className="display mt-8 text-[2.2rem] sm:text-4xl lg:text-5xl text-charcoal" data-testid="article-h1">
            <MaskLineView>{c.h1}</MaskLineView>
          </h1>
          <Reveal delay={0.2} className="mt-6">
            <p className="text-lg leading-relaxed text-ink">{c.standfirst}</p>
            <p className="mt-5 flex items-center gap-2 text-xs text-fog" data-testid="article-meta">
              <CalendarDays className="h-3.5 w-3.5 text-verde-700" /> {c.meta}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-10">
        <div className="container-x max-w-3xl">
          {c.sections.map((s, i) => (
            <Reveal key={s.h} delay={0.05} className="mt-10 first:mt-0">
              <div id={`sec-${i}`} className="scroll-mt-24">
                <h2 className="font-display text-2xl font-bold text-charcoal" data-testid={`article-section-${i}`}>{s.h}</h2>
                {s.ps.map((p, j) => <p key={j} className="mt-4 text-[15px] leading-relaxed text-ink">{p}</p>)}
                {s.list && (
                  <ul className="mt-4 space-y-2">
                    {s.list.map((li) => (
                      <li key={li} className="flex items-start gap-2.5 text-[14px] leading-relaxed text-ink">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-brass" /> {li}
                      </li>
                    ))}
                  </ul>
                )}
                {s.table && (
                  <div className="mt-5 overflow-x-auto rounded-xl border border-sand bg-white" data-testid={`article-table-${i}`}>
                    <table className="w-full min-w-[520px] text-left text-[13px]">
                      <thead>
                        <tr className="border-b border-sand bg-cream/60">
                          {s.table.head.map((hCell) => <th key={hCell} className="px-4 py-3 font-display text-xs font-bold text-charcoal">{hCell}</th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {s.table.rows.map((row, r) => (
                          <tr key={r} className="border-b border-sand/60 last:border-0">
                            {row.map((cell, ci) => <td key={ci} className={`px-4 py-3 ${ci === 0 ? "font-semibold text-charcoal" : "text-ink"}`}>{cell}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {s.faq && s.faqs && (
                  <div className="mt-6" data-testid={`article-faq-${i}`}>
                    <Faq items={s.faqs} />
                  </div>
                )}
              </div>
            </Reveal>
          ))}

          {/* Commercial block */}
          <Reveal delay={0.1} className="mt-12">
            <div className="rounded-2xl bg-verde-950 p-8 text-cream grain" data-testid="article-cta">
              <h2 className="font-display text-2xl font-bold text-white">{lang === "pt" ? "Onde encaixa a Verde Capital" : "How Verde Capital fits in"}</h2>
              <div className="mt-4 space-y-3">
                {(lang === "pt" ? [
                  "A Verde Capital fornece financiamento ao desenvolvimento de curto prazo, com garantia sénior, de €1–5m a promotores residenciais experientes em Portugal — cobrindo a aquisição de terreno, o licenciamento e a ponte até às pré-vendas e ao financiamento bancário tradicional.",
                  "Se está a preparar um projeto, descreva-o na nossa candidatura online e receberá condições indicativas em 24 horas. Todo o crédito está sujeito a subscrição, garantias e due diligence completa; nada aqui é uma oferta de crédito ou aconselhamento de investimento.",
                ] : [
                  "Verde Capital provides short-term, senior-secured development finance of €1–5m to experienced residential developers in Portugal — covering land acquisition, licensing and the bridge to pre-sales and conventional bank financing.",
                  "If you are preparing a project, describe it through our online application and you will receive indicative terms within 24 hours. All lending is subject to underwriting, security and full due diligence; nothing here is an offer of credit or investment advice.",
                ]).map((p, j) => <p key={j} className="text-sm leading-relaxed text-cream/80">{p}</p>)}
              </div>
              <div className="mt-6">
                <ArrowCta to={lp("/apply")} variant="light" testid="article-apply-cta">
                  {lang === "pt" ? "Obter condições indicativas" : "Get indicative terms"}
                </ArrowCta>
              </div>
            </div>
          </Reveal>

          {/* Sources */}
          <Reveal delay={0.1} className="mt-10">
            <h2 className="font-display text-lg font-bold text-charcoal">{lang === "pt" ? "Fontes" : "Sources"}</h2>
            <ul className="mt-3 space-y-2">
              {c.sources.map((s) => (
                <li key={s.url}>
                  <a href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[13px] text-verde-800 underline decoration-verde-800/30 underline-offset-4 hover:decoration-verde-800" data-testid="article-source">
                    {s.label} <ExternalLink className="h-3 w-3" />
                  </a>
                </li>
              ))}
            </ul>
            <p className="mt-6 border-t border-sand pt-5 text-xs leading-relaxed text-fog" data-testid="article-disclaimer">
              {lang === "pt"
                ? "Este artigo é um recurso educativo da Verde Capital e não constitui aconselhamento financeiro, legal ou fiscal. Todo o crédito está sujeito a subscrição, garantias e due diligence completa."
                : "This article is an educational resource from Verde Capital and is not financial, legal or tax advice. All lending is subject to underwriting, security and full due diligence."}
            </p>
          </Reveal>
        </div>
      </section>

      <AnalystClosing />
    </div>
  );
}
